import equal from "fast-deep-equal";
import pick from "lodash/pick";
import { useMemo } from "react";

import {
  useSearchArrayParam,
  useSearchParam,
} from "@/common/hooks/useSearchParam";
import { useTableDisplayModeParam } from "@/common/hooks/useTableDisplayMode";
import {
  deserializeColumnState,
  deserializeSorting,
  serializeColumnState,
  serializeSorting,
} from "@/common/utils/datatable";
import { ColumnSort, ColumnState } from "@reflag/shared/types/columns";

export const useDataTableParamState = <
  TSortBy extends string = string,
  TColumn extends string = string,
>({
  searchQuery: initialSearchQuery = "",
  pageIndex: initialPageIndex = 0,
  sorting: initialSortBy = [],
  columnStates: initialColumnStates = [],
  groupingColumn: initialGroupingColumn,
  allowedGroupingColumns = [],
  urlPrefix = "",
}: {
  searchQuery?: string;
  pageIndex?: number;
  sorting?: ColumnSort<TSortBy>[];
  columnStates?: ColumnState<TColumn>[];
  groupingColumn?: TColumn;
  allowedGroupingColumns?: TColumn[];
  urlPrefix?: string;
} = {}) => {
  const [searchQuery, setSearchQuery] = useSearchParam(`${urlPrefix}search`, {
    fallback: initialSearchQuery,
  });

  const [pageIndex, setPageIndex] = useSearchParam(`${urlPrefix}page`, {
    fallback: initialPageIndex,
    deserialize: (value) => {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? 0 : parsed;
    },
  });

  const [sorting, setSorting] = useSearchArrayParam<ColumnSort<TSortBy>>(
    `${urlPrefix}sorting`,
    {
      fallback: initialSortBy,
      serialize: serializeSorting,
      deserialize: deserializeSorting,
    },
  );

  const [groupingColumnParam, setGroupingColumn] = useSearchParam<TColumn>(
    `${urlPrefix}groupBy`,
    {
      allowlist: allowedGroupingColumns,
    },
  );

  const [displayMode] = useTableDisplayModeParam();

  const groupingColumn = useMemo(() => {
    if (displayMode !== "group") return undefined;
    return (
      groupingColumnParam ?? initialGroupingColumn ?? allowedGroupingColumns[0]
    );
  }, [
    displayMode,
    groupingColumnParam,
    initialGroupingColumn,
    allowedGroupingColumns,
  ]);

  const [columnStates, setColumnStates] = useSearchArrayParam<
    ColumnState<TColumn>
  >(`${urlPrefix}column`, {
    fallback: initialColumnStates,
    serialize: serializeColumnState,
    deserialize: deserializeColumnState,
  });

  return useMemo(
    () => ({
      searchQuery,
      onSearchQueryChange: (query: string | undefined) => {
        setSearchQuery(
          query && query !== initialSearchQuery ? query : undefined,
        );
      },

      pageIndex,
      onPageIndexChange: (index: number | undefined) => {
        setPageIndex(
          index && index >= 1 && index !== initialPageIndex ? index : undefined,
        );
      },

      sorting,
      onSortingChange: (sorting: ColumnSort<TSortBy>[] | undefined) => {
        setSorting(sorting && !equal(sorting, initialSortBy) ? sorting : []);
      },

      groupingColumn,
      onGroupingColumnChange: (column: TColumn | undefined) => {
        setGroupingColumn(column);
      },

      columnStates,
      onColumnStatesChange: (states: ColumnState<TColumn>[] | undefined) => {
        setColumnStates(
          states && !equal(states, initialColumnStates) ? states : [],
        );
      },

      onReset: () => {
        setSearchQuery(undefined);
        setPageIndex(undefined);
        setSorting([]);
        setColumnStates([]);
        setGroupingColumn(undefined);
      },
    }),
    [
      columnStates,
      initialColumnStates,
      initialPageIndex,
      initialSearchQuery,
      initialSortBy,
      pageIndex,
      searchQuery,
      setColumnStates,
      setPageIndex,
      setSearchQuery,
      setSorting,
      sorting,
      groupingColumn,
      setGroupingColumn,
    ],
  );
};

export type DataTableParamState<
  TSortBy extends string = string,
  TColumn extends string = string,
> = ReturnType<typeof useDataTableParamState<TSortBy, TColumn>>;

export const useSafeDataTableStateProps = <
  S extends DataTableParamState<any, any>,
>(
  state: S,
) =>
  useMemo(
    () =>
      pick(state, [
        "searchQuery",
        "onSearchQueryChange",
        "pageIndex",
        "onPageIndexChange",
        "sorting",
        "onSortingChange",
        "columnStates",
        "onColumnStatesChange",
        "groupingColumn",
      ]),
    [state],
  );
